import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api/client';

// Plan versions modal (Production Plan). GET /plantoproduce/plan-versions → list;
// POST /plantoproduce/plan-versions { note } → manual snapshot; POST .../:id/restore → onRestored() (parent refetches the grid and closes).

const errMsg = (e, fallback) => (e.response && e.response.data && e.response.data.error) || fallback;
const fmtN = n => Number(n || 0).toLocaleString('en-US');

const PlanVersionsModal = ({ onClose, onRestored }) => {
  const [versions, setVersions] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);   // save / restore in flight
  const [err, setErr] = useState(null);
  const downOnOverlay = useRef(false);   // close only when mousedown AND click land on the overlay

  const load = useCallback(async () => {
    try {
      const r = await api.get('/plantoproduce/plan-versions', { params: { limit: 50 } });
      setVersions((r.data && r.data.versions) || []);
    } catch (e) {
      console.error('Error loading plan versions:', e);
      setErr(errMsg(e, 'Failed to load versions'));
      setVersions([]);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape' && !busy) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, busy]);

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api.post('/plantoproduce/plan-versions', { note: note.trim() });
      setNote('');
      await load();
    } catch (e) {
      console.error('Error saving plan version:', e);
      setErr(errMsg(e, 'Failed to save the version'));
    } finally { setBusy(false); }
  };

  const restore = async v => {
    if (!window.confirm(`Restore the plan from ${new Date(v.created_at).toLocaleString()} (days from ${String(v.from_date).slice(0, 10)})? Current plan is backed up first.`)) return;
    setBusy(true);
    setErr(null);
    try {
      await api.post(`/plantoproduce/plan-versions/${v.id}/restore`);
      onRestored();
    } catch (e) {
      console.error('Error restoring plan version:', e);
      setErr(errMsg(e, 'Failed to restore the version'));
      setBusy(false);
    }
  };

  return (
    <div className="pp-modal-overlay"
         onMouseDown={e => { downOnOverlay.current = e.target === e.currentTarget; }}
         onClick={e => { if (downOnOverlay.current && e.target === e.currentTarget && !busy) onClose(); }}>
      <div className="pp-modal pp-modal-wide">
        <div className="pp-modal-title">Plan history</div>
        <div className="pp-modal-row pp-vsave">
          <input type="text" placeholder="Note (optional)" value={note} maxLength={500} autoFocus
                 onChange={e => setNote(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !busy) save(); }} />
          <button className="pp-btn pp-btn-primary" onClick={save} disabled={busy}>Save version</button>
        </div>
        <div className="pp-vlist">
          <table className="pp-vtable">
            <thead>
              <tr><th>Date</th><th>Source</th><th>Actor</th><th>Note</th><th className="pp-vnum">Rows</th><th className="pp-vnum">Total qty</th><th /></tr>
            </thead>
            <tbody>
              {versions === null && <tr><td colSpan={7} className="pp-muted">Loading…</td></tr>}
              {versions && versions.length === 0 && <tr><td colSpan={7} className="pp-muted">No saved versions yet</td></tr>}
              {versions && versions.map(v => (
                <tr key={v.id}>
                  <td title={`#${v.id} · from ${String(v.from_date).slice(0, 10)}`}>{new Date(v.created_at).toLocaleString()}</td>
                  <td><span className={`pp-badge pp-badge-${v.source}`}>{v.source}</span></td>
                  <td>{v.actor || '—'}</td>
                  <td className="pp-vnote" title={v.note || ''}>{v.note || ''}</td>
                  <td className="pp-vnum">{fmtN(v.row_count)}</td>
                  <td className="pp-vnum">{fmtN(v.total_qty)}</td>
                  <td><button className="pp-btn pp-btn-sm" onClick={() => restore(v)} disabled={busy}>Restore</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {err && <div className="pp-modal-err">{err}</div>}
        <div className="pp-modal-actions">
          <button className="pp-btn" onClick={onClose} disabled={busy}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default PlanVersionsModal;
